/**
 * pdf.js — client-side certificate PDF generation using html2canvas + jsPDF.
 *
 * Advantages over server-side reportlab:
 *  - identical visual to the certificate viewer
 *  - no Arabic-font packaging pain
 *  - works for bulk (looped PNGs → single multi-page PDF)
 */
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/** Render one DOM node to a single PDF page, preserving its natural aspect
 * ratio with a uniform margin on all sides. By default the content is
 * vertically centered (fitting for a single certificate); pass
 * alignTop:true for tabular reports, which should always start at a fixed
 * top position and only extend downward as content grows — never having
 * a top margin that changes with the amount of data.
 * format/orientation: page size passed through to jsPDF's addPage() calls
 * too (must match the format the pdf itself was constructed with) —
 * defaults to "a4"/"p" exactly as before for every existing caller.
 * scale/marginMm: كانا مثبَّتين (3، 10مم) للجميع — أصبحا قابلَين للتخصيص
 * (لتقارير عريضة جدًا تحتاج هامشًا أقل ودقة أقل لتفادي تجميد المتصفح)، مع
 * إبقاء نفس القيم الافتراضية القديمة تمامًا لكل الاستخدامات الأخرى. */
async function renderNodeToDoc(node, pdf, { pageIndex = 0, orientation = "p", alignTop = false, format = "a4", scale = 3, marginMm = 10 } = {}) {
  const canvas = await html2canvas(node, {
    // scale أعلى (3x افتراضيًا) + PNG بدل JPEG: ضغط JPEG (Lossy) يُطمّس
    // الخطوط الرفيعة التي تصل بين الحروف العربية المتصلة، فتبدو الحروف
    // وكأنها مقطّعة أو منفصلة عن بعضها. PNG غير مضغوط (Lossless) يحافظ
    // على حدة هذه التفاصيل الدقيقة تمامًا.
    scale, useCORS: true, backgroundColor: "#ffffff", logging: false,
  });
  // مهم جدًا (سبب خطأ "Invalid string length" سابقًا في الشهادات الجماعية
  // لعدد كبير من الطلاب): كنا نحوّل كل Canvas إلى نص Base64 ضخم عبر
  // canvas.toDataURL()، ثم نراكم عشرات هذه النصوص (كل واحد عدة ميجابايت)
  // داخل نفس مستند jsPDF. jsPDF يبني الملف النهائي بتجميع كل هذا كنص JS
  // واحد ضخم عند buildDocument/save — فيتجاوز الحد الأقصى لطول أي String
  // واحد في محرك JavaScript (V8) قبل حتى إنشاء ملف PDF فعليًا. تمرير
  // عنصر الـ <canvas> نفسه مباشرة لـ jsPDF.addImage (بدل تحويله لنص أولًا)
  // يجعل jsPDF يتعامل مع بيانات الصورة كبيانات ثنائية (Typed Array)
  // داخليًا بدل نص JS — فيتفادى المشكلة جذريًا للشهادات الجماعية الكبيرة،
  // بلا أي تغيير في الدقة أو التصميم أو الجودة المرئية. أضفنا أيضًا ضغط
  // PDF داخلي (compression: MEDIUM، ضغط Deflate بلا فقد جودة) كطبقة أمان
  // إضافية تقلّل حجم الملف النهائي.
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const usableWidth = pageWidth - marginMm * 2;
  const usableHeight = pageHeight - marginMm * 2;
  const imgRatio = canvas.height / canvas.width;
  const pageRatio = usableHeight / usableWidth;

  // فرق أقل من 1% بين نسبة العنصر ونسبة المساحة المتاحة يعني أن العنصر
  // مبني أصلًا بنفس نسبة الصفحة دقيقًا (كالشهادات، المبنية على مقاس
  // 794×1123 لتطابق A4) — نملأ المساحة المتاحة كاملة مباشرة بلا أي حساب
  // إضافي، فلا احتمال للتشويه أو التصغير الاجتهادي إطلاقًا.
  if (Math.abs(imgRatio - pageRatio) / pageRatio < 0.01) {
    if (pageIndex > 0) pdf.addPage(format, orientation);
    pdf.addImage(canvas, "PNG", marginMm, marginMm, usableWidth, usableHeight, undefined, "MEDIUM");
    return;
  }

  const imgWidth = usableWidth;
  const imgHeight = Math.min(imgWidth * imgRatio, usableHeight);
  const y = alignTop ? marginMm : marginMm + (usableHeight - imgHeight) / 2;
  if (pageIndex > 0) pdf.addPage(format, orientation);
  pdf.addImage(canvas, "PNG", marginMm, y, imgWidth, imgHeight, undefined, "MEDIUM");
}

/** Download a single certificate/report PDF from a DOM node.
 * format/orientation: e.g. {format:"a3", orientation:"l"} for wide tabular
 * reports — defaults to "a4"/"p" exactly as before when omitted, so every
 * existing caller (certificates, etc.) is completely unaffected. */
export async function downloadCertificatePdf(node, filename = "certificate.pdf", opts = {}) {
  const { format = "a4", orientation = "p", ...renderOpts } = opts;
  const pdf = new jsPDF({ orientation, unit: "mm", format });
  await renderNodeToDoc(node, pdf, { pageIndex: 0, format, orientation, ...renderOpts });
  pdf.save(filename);
}

/** Bulk render: hand it an ordered list of DOM nodes → single PDF file. */
export async function downloadBulkCertificatesPdf(nodes, filename = "class-certificates.pdf") {
  if (!nodes.length) return;
  const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  for (let i = 0; i < nodes.length; i++) {
    await renderNodeToDoc(nodes[i], pdf, { pageIndex: i });
  }
  pdf.save(filename);
}

// ══════════════════════ TABULAR / MULTI-PAGE REPORTS ══════════════════════
// كشوف الرصد (وأي تقرير جدولي مشابه) قد يطول جدولها لعدد كبير من الطلاب —
// بعكس الشهادات ذات المقاس الثابت، لا نريد ضغط الجدول كله في صفحة واحدة
// (renderNodeToDoc أعلاه يفعل هذا الضغط عمدًا، وهو مناسب للشهادات فقط).
// الدوال التالية منفصلة تمامًا، ولا تُستخدَم إلا من صفحات الكشوف الجدولية:
// تعرض المحتوى بحجمه الطبيعي (بدون أي تصغير)، وتوزّعه على عدة صفحات عند
// الحاجة — بعدد ثابت من الصفوف الكاملة لكل صفحة (لا يُقطَع اسم أي طالب
// بين صفحتين أبدًا)، مع تكرار كل ما هو قبل وحتى نهاية <thead> (الشعار/
// العنوان/رؤوس الأعمدة) في أعلى كل صفحة تالية للحفاظ على المظهر.

const DEFAULT_ROWS_PER_PAGE = 10;

async function renderPaginatedNodeToDoc(node, pdf, { pageOffset = 0, orientation = "p", rowsPerPage = DEFAULT_ROWS_PER_PAGE } = {}) {
  const canvas = await html2canvas(node, {
    scale: 3, useCORS: true, backgroundColor: "#ffffff", logging: false,
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginMm = 10;
  const usableWidthMm = pageWidth - marginMm * 2;
  const usableHeightMm = pageHeight - marginMm * 2;
  const mmPerPx = usableWidthMm / canvas.width;

  // نقيس ارتفاع "منطقة الترويسة المتكررة" (كل شيء حتى نهاية <thead>)،
  // وحدود كل صف (<tr>) داخل <tbody> من الـ DOM الحي قبل تحويله لصورة، ثم
  // نحوّل هذه القياسات لنفس مقياس الكانفاس (نحسبه فعليًا من النسبة بدل
  // افتراض scale:3 حرفيًا، للأمان).
  const theadEl = node.querySelector("thead");
  const tbodyEl = node.querySelector("tbody");
  const nodeRect = node.getBoundingClientRect();
  const pxScaleFactor = canvas.width / nodeRect.width;
  const headerHeightPx = theadEl
    ? Math.round((theadEl.getBoundingClientRect().bottom - nodeRect.top) * pxScaleFactor)
    : 0;
  const rowEls = tbodyEl ? Array.from(tbodyEl.querySelectorAll("tr")) : [];
  const rowBoundaries = rowEls.map((tr) => {
    const r = tr.getBoundingClientRect();
    return {
      top: Math.round((r.top - nodeRect.top) * pxScaleFactor),
      bottom: Math.round((r.bottom - nodeRect.top) * pxScaleFactor),
    };
  });

  if (!headerHeightPx || !rowBoundaries.length) {
    // لا يوجد <thead>/<tbody> (عنصر غير جدولي) — نعرضه بحجمه الطبيعي في
    // صفحة واحدة كأفضل محاولة، بدل تقسيمه بلا معنى.
    const imgHeightMm = Math.min(canvas.height * mmPerPx, usableHeightMm);
    if (pageOffset > 0) pdf.addPage("a4", orientation);
    pdf.addImage(canvas, "PNG", marginMm, marginMm, usableWidthMm, imgHeightMm, undefined, "MEDIUM");
    return 1;
  }

  // تقسيم الصفوف لمجموعات بعدد ثابت (rowsPerPage) — القطع دائمًا عند حد
  // صف كامل، فلا يظهر اسم طالب مقطوعًا بين صفحتين أبدًا.
  const chunks = [];
  for (let i = 0; i < rowBoundaries.length; i += rowsPerPage) {
    chunks.push(rowBoundaries.slice(i, i + rowsPerPage));
  }

  for (let p = 0; p < chunks.length; p++) {
    const chunk = chunks[p];
    const sliceStart = chunk[0].top;
    const sliceHeight = chunk[chunk.length - 1].bottom - sliceStart;

    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = headerHeightPx + sliceHeight;
    const ctx = pageCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    // الترويسة (تُعاد رسمها من نفس الصورة الأصلية في أعلى كل صفحة)
    ctx.drawImage(canvas, 0, 0, canvas.width, headerHeightPx, 0, 0, canvas.width, headerHeightPx);
    // مجموعة الصفوف الكاملة الخاصة بهذه الصفحة تحديدًا
    ctx.drawImage(canvas, 0, sliceStart, canvas.width, sliceHeight, 0, headerHeightPx, canvas.width, sliceHeight);

    // إصلاح مقاس A4/الهوامش: نحسب أبعاد الرسم بحيث لا تتجاوز مساحة
    // الطباعة المتاحة في أي من البُعدين مهما كانت النسبة (ضمان صارم،
    // وليس افتراضًا) — هذا يمنع أي احتمال لخروج جزء من الجدول عن حواف
    // الصفحة (وبالتالي تآكل الهامش الأيمن أو السفلي). كما نُوسِّط الرسم
    // أفقيًا دائمًا (بدل التثبيت من اليسار فقط) — فلو احتاج المحتوى
    // تصغيرًا لأي سبب، يبقى الهامشان الأيمن والأيسر متساويين تلقائيًا،
    // بدل تراكم كل مساحة التصغير على جانب واحد فقط. القيم الافتراضية
    // (10 صفوف/صفحة بالحجم الطبيعي) لا تحتاج أي تصغير عمليًا؛ هذا فقط
    // ضمان صارم لكل الحالات.
    let drawWidthMm = usableWidthMm;
    let imgHeightMm = pageCanvas.height * mmPerPx;
    if (imgHeightMm > usableHeightMm) {
      const shrink = usableHeightMm / imgHeightMm;
      drawWidthMm *= shrink;
      imgHeightMm = usableHeightMm;
    }
    drawWidthMm = Math.min(drawWidthMm, usableWidthMm);
    imgHeightMm = Math.min(imgHeightMm, usableHeightMm);
    const xMm = marginMm + (usableWidthMm - drawWidthMm) / 2;
    const yMm = marginMm; // ثابت أعلى الصفحة دائمًا — الترويسة تبدأ من نفس النقطة في كل صفحة

    if (pageOffset + p > 0) pdf.addPage("a4", orientation);
    pdf.addImage(pageCanvas, "PNG", xMm, yMm, drawWidthMm, imgHeightMm, undefined, "MEDIUM");
  }
  return chunks.length;
}

/** Download a single tabular report (e.g. a grade sheet) that may span
 * multiple pages at natural size, with a fixed number of complete rows per
 * page (default 10 — no row/name is ever split across pages), repeating
 * the header/column-titles on every page. */
export async function downloadPaginatedReportPdf(node, filename = "report.pdf", opts = {}) {
  const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  await renderPaginatedNodeToDoc(node, pdf, { pageOffset: 0, ...opts });
  pdf.save(filename);
}

/** Bulk version: several tabular nodes (e.g. one per subject), each
 * possibly spanning multiple pages, combined into a single PDF. */
export async function downloadBulkPaginatedReportPdf(nodes, filename = "reports.pdf", opts = {}) {
  if (!nodes.length) return;
  const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  let pageCount = 0;
  for (const node of nodes) {
    pageCount += await renderPaginatedNodeToDoc(node, pdf, { pageOffset: pageCount, ...opts });
  }
  pdf.save(filename);
}
